import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";

import { GlobalState } from "context/GlobalContext";
import axios from "axios";
import { PimDashboardState } from "context/PimDashboardContext";
import { attrGroupLinkedTables, productionStageOptions } from "components/PimDashboard/data";
import {
	arrType,
	converAttrToDatagridCol,
	createdByColumns,
	imageTableColumn,
	inputTypes,
	publishColumns,
	selectOptionsKeyName,
	stageColumn as stageAttrMeta,
	statusOptions,
	styleFabricUpdationColumns,
	styleMrpUpdationColumns,
	styleImageColumn,
} from "data/globalData";
import {
	ChevronLeftRounded,
	ChevronRightRounded,
	FileDownloadRounded,
	FilterAltRounded,
	InfoRounded,
	Publish,
	PublishRounded,
	UploadRounded,
	ViewColumnRounded,
	CloseRounded,
	OpenInNewRounded,
} from "@mui/icons-material";
import FilterModal from "./FilterModal/FilterModal";
import AddColumnModal from "./AddColumnModal/AddColumnModal";
import { AuthState } from "context/AuthContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { MRP_SHEET_API, mrpSheetFilterValStr, mrpSheetFilterValStrLegacy, mrpSheetExportOptions } from "./data";
import { hasRemarkFilter } from "components/PimDashboard/DashboardComponents/Designer/StylesDashboard/data";
import DataExportModal from "components/PimDashboard/DashboardComponents/Designer/StylesDashboard/DataExportModal/DataExportModal";
import EditDetailsModal from "./EditDetailsModal/EditDetailsModal";
import ViewEditRemarkModal from "components/PimDashboard/DashboardComponents/Designer/StylesDashboard/ViewEditRemarkModal/ViewEditRemarkModal";
import MrpUploadModal from "./UploadModal/UploadModal";
import UploadResultModal from "./UploadResultModal/UploadResultModal";
import BulkPublishModal from "./BulkPublishModal/BulkPublishModal";
import { admin, custom, services } from "data/authData";
import { allowAccess } from "utils/userAccessFun";

const PAGE_SIZE = 20;

const readStoredFilters = () => {
	const raw = localStorage.getItem(mrpSheetFilterValStr) || localStorage.getItem(mrpSheetFilterValStrLegacy);
	if (!raw) return {};
	try {
		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
	} catch (e) {
		return {};
	}
};

const persistFilters = (obj) => {
	try {
		if (obj && Object.keys(obj).length) {
			localStorage.setItem(mrpSheetFilterValStr, JSON.stringify(obj));
		} else {
			localStorage.removeItem(mrpSheetFilterValStr);
		}
		// Stop sharing / overwriting the Styles dashboard key.
		localStorage.removeItem(mrpSheetFilterValStrLegacy);
	} catch (e) {
		/* ignore */
	}
};

const palette = {
	ink: "#111110",
	ink2: "#3a3a38",
	ink3: "#6b6b68",
	ink4: "#9b9b97",
	surface: "#ffffff",
	surface2: "#f7f7f5",
	surface3: "#f0efec",
	border: "rgba(0,0,0,0.10)",
	border2: "rgba(0,0,0,0.14)",
	green: "#2a7a4f",
	greenBg: "#eaf4ee",
	red: "#b83232",
	redBg: "#fdeaea",
};

const colStyle = {
	headerAlign: "left",
	align: "left",
};

const formatCount = (n) => Number(n || 0).toLocaleString("en-US");

const formatFilterChipValue = (value) => {
	if (Array.isArray(value)) return value.join(", ");
	if (value == null) return "";
	return String(value);
};

const StatusPill = ({ status }) => {
	const option = statusOptions[status];
	const label = option?.name || status || "Pending";
	const bg = option?.color || palette.redBg;
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				px: 1,
				py: 0.25,
				borderRadius: 999,
				fontSize: 11,
				fontWeight: 600,
				lineHeight: 1.4,
				bgcolor: bg,
				color: "#fff",
				whiteSpace: "nowrap",
			}}>
			{label}
		</Box>
	);
};

const STAGE_PILL_TONES = {
	STYLE_ATTRIBUTES: { bg: "#e8f0fa", color: "#2f5f8f" },
	BUYING: { bg: "#f5f0e6", color: "#8a6a2b" },
	FABRIC_TRIMS: { bg: "#f0ebe4", color: "#6b5540" },
	SOURCING: { bg: "#eef5f0", color: "#3d6b4f" },
};

const StagePill = ({ stageKey, label }) => {
	const tone = STAGE_PILL_TONES[stageKey] || { bg: palette.surface3, color: palette.ink2 };
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				px: 1,
				py: 0.25,
				borderRadius: 999,
				fontSize: 11,
				fontWeight: 600,
				lineHeight: 1.4,
				bgcolor: tone.bg,
				color: tone.color,
				whiteSpace: "nowrap",
			}}>
			{label || "—"}
		</Box>
	);
};

const ToolbarButton = ({ icon, label, onClick }) => (
	<Button
		variant="outlined"
		size="small"
		onClick={onClick}
		startIcon={icon}
		sx={{
			textTransform: "none",
			fontSize: 12,
			fontWeight: 500,
			color: palette.ink2,
			borderColor: palette.border2,
			bgcolor: palette.surface2,
			borderRadius: 1.25,
			px: 1.25,
			py: 0.5,
			minHeight: 32,
			"&:hover": { borderColor: palette.ink3, bgcolor: palette.surface3 },
		}}>
		{label}
	</Button>
);

/**
 * MRP styles list — Styles-dashboard chrome + 20-row server pagination.
 * @param onFilterChange - { filter, search } so MrpAnalytics scopes to the same More Filters.
 * @param onFilterUiChange - { open, count } so the shell can put More Filters on the analytics header.
 */
const MrpSheet = ({ onFilterChange, onFilterUiChange }) => {
	const requestIdRef = useRef(0);
	const prevSelectionRef = useRef([]);
	const publishTargetRef = useRef({ selectAll: false, styleIds: [] });
	const { allAttributes, allAttributesObj } = PimDashboardState();
	const { user } = AuthState();
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const [styles, setStyles] = useState([]);
	const [displayImg, setDisplayImg] = useState(true);
	const [tableStatusCount, setTableStatusCount] = useState({
		total: 0,
		pending: 0,
		published: 0,
	});
	const [filterModal, setFilterModal] = useState(false);
	const [addColumnModal, setAddColumnModal] = useState(false);
	const [editModal, setEditModal] = useState(false);
	const [selectedStyle, setSelectedStyle] = useState({});
	const [viewEditRemarkModal, setViewEditRemarkModal] = useState(false);
	const [selectedStyleForRemark, setSelectedStyleForRemark] = useState(null);
	const [uploadModal, setUploadModal] = useState(false);
	const [uploadResultModal, setUploadResultModal] = useState(false);
	const [uploadResult, setUploadResult] = useState(null);
	const [exportModal, setExportModal] = useState(false);
	const [selectedStyleIds, setSelectedStyleIds] = useState([]);
	const [selectAllFiltered, setSelectAllFiltered] = useState(false);
	const [selectAllCount, setSelectAllCount] = useState(0);
	const [bulkPublishModal, setBulkPublishModal] = useState(false);
	const [bulkPublishPreview, setBulkPublishPreview] = useState([]);
	const [bulkPublishLoading, setBulkPublishLoading] = useState(false);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({
		[styleImageColumn.key]: true,
	});
	const [zoomedImage, setZoomedImage] = useState(null);

	const canEditMrp = user?.role?.[admin] === true || user?.role?.[custom.MRP_SHEET_VIEWER.key] !== true;
	const canPublishMrp = !!user && allowAccess(custom.MRP_SHEET_PUBLISHER.key, user, services.BUYING.key);

	const selectableStyleIds = styles
		.filter((row) => row?.status === statusOptions.MRP_PENDING.key)
		.map((row) => row.style_id);

	const selectionModel = selectAllFiltered ? selectableStyleIds : selectedStyleIds;
	const selectedCount = selectAllFiltered ? selectAllCount : selectedStyleIds.length;

	const defColumns = [
		{
			field: "color_count",
			headerName: "Colors",
			width: 80,
			...colStyle,
		},
		...styleMrpUpdationColumns.map((row) => converAttrToDatagridCol(row)),
	];

	const [styleTablColumns, setStyleTableColumns] = useState(defColumns);

	const [attrColumns, setAttrColumns] = useState(() => {
		if (localStorage.getItem("styles_table_attr")) {
			return (
				JSON.parse(localStorage.getItem("styles_table_attr"))
					?.map((row) => {
						return { ...allAttributesObj?.[row] };
					})
					.map((row) => converAttrToDatagridCol(row))
					.filter((col) => col?.field) || []
			);
		}
		return (allAttributes || [])
			.filter((row) => row?.linked_table === attrGroupLinkedTables.style.key)
			.map((row) => converAttrToDatagridCol(row));
	});

	const [filterColumns, setFilterColumns] = useState(() => {
		const localStorageObj = readStoredFilters();
		if (!Object.keys(localStorageObj).length) return [];
		// Stage options inlined — avoid touching productionStageOptions during module init cycles.
		const stageFilterMeta = {
			...stageAttrMeta,
			input_type: inputTypes.SELECT.key,
			[selectOptionsKeyName]: {
				type: arrType,
				list: [
					{ name: "Style Attributes", key: "STYLE_ATTRIBUTES" },
					{ name: "Buying", key: "BUYING" },
					{ name: "Fabric & Trims", key: "FABRIC_TRIMS" },
					{ name: "Sourcing", key: "SOURCING" },
				],
			},
		};
		const catalog = [
			stageFilterMeta,
			...(allAttributes || []),
			...createdByColumns,
			hasRemarkFilter,
			...Object.keys(publishColumns).map((row) => publishColumns[row]),
			...styleFabricUpdationColumns,
			...styleMrpUpdationColumns,
		];
		return catalog.filter((row) => row?.key && localStorageObj[row.key]);
	});
	const [filterColumnVal, setFilterColumnVal] = useState(() => readStoredFilters());

	const filterRef = useRef(filterColumnVal);
	const attrColumnsRef = useRef(attrColumns);
	filterRef.current = filterColumnVal;
	attrColumnsRef.current = attrColumns;

	useEffect(() => {
		// One-time migrate off the shared Styles localStorage key.
		const stored = filterColumnVal || {};
		if (Object.keys(stored).length) persistFilters(stored);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const activeFilterCount = filterColumns?.length || 0;
	const totalPages = Math.max(1, Math.ceil(Number(tableStatusCount.total || 0) / PAGE_SIZE) || 1);
	const showingFrom = tableStatusCount.total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
	const showingTo = Math.min(page * PAGE_SIZE, Number(tableStatusCount.total || 0));

	const pageButtons = useMemo(() => {
		const windowSize = Math.min(5, totalPages);
		const start = Math.min(Math.max(1, page - Math.floor(windowSize / 2)), Math.max(1, totalPages - windowSize + 1));
		return Array.from({ length: windowSize }, (_, i) => start + i);
	}, [page, totalPages]);

	const clearSelection = () => {
		setSelectedStyleIds([]);
		setSelectAllFiltered(false);
		setSelectAllCount(0);
		prevSelectionRef.current = [];
	};

	const handleCloseFilterModal = () => setFilterModal(false);
	const handleCloseAddColumnModal = () => setAddColumnModal(false);
	const handleCloseExportModal = () => setExportModal(false);
	const handleFilterClick = useCallback(() => setFilterModal(true), []);
	const handleAddColumnClick = () => setAddColumnModal(true);
	const handleDownloadClick = () => setExportModal(true);

	useEffect(() => {
		onFilterUiChange?.({ open: handleFilterClick, count: activeFilterCount });
	}, [onFilterUiChange, handleFilterClick, activeFilterCount]);

	useEffect(() => {
		onFilterChange?.({ filter: filterColumnVal || {}, search: "" });
	}, [filterColumnVal, onFilterChange]);

	const removeActiveFilter = (key) => {
		setFilterColumns((prev) => (prev || []).filter((row) => row.key !== key));
		setFilterColumnVal((prev) => {
			const next = { ...(prev || {}) };
			delete next[key];
			persistFilters(next);
			return next;
		});
	};

	const handleRemarkClick = (e, styleData) => {
		e.stopPropagation();
		setSelectedStyleForRemark(styleData);
		setViewEditRemarkModal(true);
	};

	const canPublishRow = useCallback(
		(row) => {
			if (!canPublishMrp) return false;
			return row?.status === statusOptions.MRP_PENDING.key;
		},
		[canPublishMrp],
	);

	const openPublishPreview = useCallback(
		async ({ selectAll = false, styleIds = [] } = {}) => {
			const filter = filterRef.current || {};
			publishTargetRef.current = { selectAll, styleIds };
			setBulkPublishLoading(true);
			setLoadingOpen(true);
			try {
				const { data } = await axios.post(`${MRP_SHEET_API}/bulk_publish_preview`, {
					select_all: selectAll,
					style_ids: selectAll ? undefined : styleIds,
					filter: selectAll ? filter : undefined,
				});
				setBulkPublishPreview(data?.result?.styles || []);
				setBulkPublishModal(true);
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar((prev) => ({
					...prev,
					severity: "error",
					message: parseErrorMessage(error),
				}));
			} finally {
				setBulkPublishLoading(false);
				setLoadingOpen(false);
			}
		},
		[setLoadingOpen, setSnackBar, setSnackBarOpen],
	);

	const handleRowPublishClick = (e, row) => {
		e.stopPropagation();
		if (!row?.style_id) return;
		setSelectAllFiltered(false);
		setSelectedStyleIds([row.style_id]);
		prevSelectionRef.current = [row.style_id];
		openPublishPreview({ selectAll: false, styleIds: [row.style_id] });
	};

	const statusColumn = useMemo(
		() => ({
			field: "status",
			headerName: "Status",
			width: 200,
			minWidth: 200,
			flex: 0,
			sortable: false,
			...colStyle,
			renderCell: ({ row }) => {
				const showPublish = canPublishRow(row);
				const hasRemark = !!row?.remark_id;
				return (
					<Box display="flex" alignItems="center" height="100%" columnGap={0.5} onClick={(e) => e.stopPropagation()}>
						<StatusPill status={row?.status} />
						{hasRemark && (
							<IconButton size="small" color="warning" sx={{ p: 0.25 }} onClick={(e) => handleRemarkClick(e, row)}>
								<InfoRounded sx={{ fontSize: 16 }} />
							</IconButton>
						)}
						{showPublish && (
							<Button
								size="small"
								variant="text"
								startIcon={<Publish sx={{ fontSize: 14 }} />}
								onClick={(e) => handleRowPublishClick(e, row)}
								sx={{ textTransform: "none", fontSize: 11, minWidth: 0, px: 0.5, color: palette.ink2 }}>
								Publish
							</Button>
						)}
					</Box>
				);
			},
		}),
		// handleRowPublishClick / handleRemarkClick are stable enough for column rebuild via canPublishRow
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[canPublishRow, openPublishPreview],
	);

	const stageGridColumn = useMemo(
		() => ({
			field: "stage",
			headerName: "Stage",
			width: 140,
			minWidth: 140,
			flex: 0,
			sortable: false,
			...colStyle,
			renderCell: ({ row }) => {
				const stageKey = row?.stage;
				const label = productionStageOptions?.[stageKey]?.name || stageKey || "—";
				return (
					<Box display="flex" alignItems="center" height="100%">
						<StagePill stageKey={stageKey} label={label} />
					</Box>
				);
			},
		}),
		[],
	);

	const fetchPage = useCallback(
		async (nextPage = 1, { clearSel = false } = {}) => {
			const filter = filterRef.current || {};
			const extraColumns = attrColumnsRef.current?.reduce((acc, row) => {
				if (row?.field) acc[row.field] = true;
				return acc;
			}, {});
			const requestId = ++requestIdRef.current;
			const offset = (nextPage - 1) * PAGE_SIZE;
			setLoading(true);
			setLoadingOpen(true);
			try {
				const { data } = await axios.post(`${MRP_SHEET_API}/styles/${offset}`, {
					filter,
					extraColumns,
					limit: PAGE_SIZE,
					paginate: true,
				});
				if (requestId !== requestIdRef.current) return;
				const rows = data?.result?.styles || [];
				const count = data?.result?.count?.[0] || { total: 0, published: 0, pending: 0 };
				setTableStatusCount(count);
				setStyles(rows);
				setPage(nextPage);
				if (clearSel) clearSelection();
			} catch (error) {
				if (requestId !== requestIdRef.current) return;
				setSnackBarOpen(true);
				setSnackBar((prev) => ({ ...prev, severity: "error", message: parseErrorMessage(error) }));
			} finally {
				if (requestId === requestIdRef.current) {
					setLoading(false);
					setLoadingOpen(false);
				}
			}
		},
		[setLoadingOpen, setSnackBar, setSnackBarOpen],
	);

	const filterKey = useMemo(() => JSON.stringify(filterColumnVal || {}), [filterColumnVal]);
	const attrColumnsKey = useMemo(
		() =>
			(attrColumns || [])
				.map((col) => col?.field)
				.filter(Boolean)
				.join("|"),
		[attrColumns],
	);

	const handleImageZoom = useCallback((row, url) => {
		setZoomedImage({ url, styleId: row?.style_id, styleName: row?.style_name });
	}, []);

	const reloadStyles = async () => {
		await fetchPage(page || 1, { clearSel: true });
	};

	const goToPage = (p) => {
		const next = Math.min(Math.max(1, p), totalPages);
		if (next !== page) fetchPage(next);
	};

	useEffect(() => {
		setStyleTableColumns([
			statusColumn,
			stageGridColumn,
			imageTableColumn(styleImageColumn, { onZoom: handleImageZoom }),
			...(attrColumns || []).filter((col) => col?.field && col.field !== "stage"),
			...defColumns,
		]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [attrColumns, statusColumn, stageGridColumn, handleImageZoom]);

	useEffect(() => {
		setColumnVisibilityModel((prev) => ({
			...prev,
			[styleImageColumn.key]: displayImg,
		}));
	}, [displayImg]);

	useEffect(() => {
		fetchPage(1, { clearSel: true });
		// filterKey / attrColumnsKey force a refetch whenever filter values or columns change
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterKey, attrColumnsKey]);

	const handleStyleSelect = async (data) => {
		setSelectedStyle(data);
		setEditModal(true);
	};

	const handleUploadSave = async (rows, { markAsPublished = false, idType = "style_id" } = {}) => {
		const validRows = rows.filter((row) => row.__isValid !== false);

		if (!validRows.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message:
					idType === "style_id"
						? "Upload file must contain valid style_id and final_mrp values"
						: idType === "option_id"
							? "Upload file must contain valid option_id and final_mrp values"
							: "Upload file must contain valid style_ref_no and final_mrp values",
				severity: "warning",
			});
			return false;
		}

		setLoadingOpen(true);
		try {
			const response = await axios.post(`${MRP_SHEET_API}/bulk_upload_final_mrp`, {
				mark_as_published: markAsPublished,
				id_type: idType,
				rows: validRows,
			});
			const responseData = response?.data || {};

			if (responseData.success === false) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: responseData.message || "Upload failed",
					severity: "error",
				});
				return responseData;
			}

			const result = responseData?.result || {};
			const updated = result.updated ?? 0;
			const skipped = result.skipped_unchanged ?? 0;
			const published = result.published ?? 0;

			setUploadResult(result);
			setUploadResultModal(true);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Upload complete: ${updated} updated, ${skipped} unchanged, ${published} published`,
				severity: updated === 0 && skipped > 0 ? "info" : "success",
			});
			setUploadModal(false);
			reloadStyles();
			return responseData;
		} catch (error) {
			const errorData = error?.response?.data;
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: errorData?.message || parseErrorMessage(error),
				severity: "error",
			});
			return errorData || false;
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleStyleUpdate = () => {
		reloadStyles();
	};

	const handleSelectAllFiltered = async () => {
		setLoadingOpen(true);
		try {
			const { data } = await axios.post(`${MRP_SHEET_API}/bulk_publish_preview`, {
				select_all: true,
				filter: filterRef.current || {},
			});
			const stylesToPublish = data?.result?.styles || [];
			setSelectAllFiltered(true);
			setSelectAllCount(stylesToPublish.length);
			setSelectedStyleIds(stylesToPublish.map((row) => row.style_id));
			prevSelectionRef.current = styles
				.filter((row) => row?.status === statusOptions.MRP_PENDING.key)
				.map((row) => row.style_id);
			setSnackBarOpen(true);
			setSnackBar((prev) => ({
				...prev,
				severity: "info",
				message: `${stylesToPublish.length} MRP_PENDING style(s) selected from current filter`,
			}));
		} catch (error) {
			clearSelection();
			setSnackBarOpen(true);
			setSnackBar((prev) => ({
				...prev,
				severity: "error",
				message: parseErrorMessage(error),
			}));
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleSelectionChange = (newSelectionModel) => {
		const nextSelection = Array.isArray(newSelectionModel) ? newSelectionModel : [];
		const prevSelection = prevSelectionRef.current;
		const selectableOnPage = selectableStyleIds;

		if (nextSelection.length === 0) {
			clearSelection();
			return;
		}

		const selectedAllLoaded =
			selectableOnPage.length > 0 &&
			nextSelection.length === selectableOnPage.length &&
			selectableOnPage.every((id) => nextSelection.includes(id));

		const previouslyHadAllLoaded =
			selectableOnPage.length > 0 &&
			prevSelection.length === selectableOnPage.length &&
			selectableOnPage.every((id) => prevSelection.includes(id));

		if (selectedAllLoaded && !previouslyHadAllLoaded && !selectAllFiltered) {
			handleSelectAllFiltered();
			return;
		}

		if (selectAllFiltered && !selectedAllLoaded) {
			setSelectAllFiltered(false);
			setSelectAllCount(0);
		}

		setSelectedStyleIds(nextSelection);
		prevSelectionRef.current = nextSelection;
	};

	const handleBulkPublishClick = async () => {
		await openPublishPreview({
			selectAll: selectAllFiltered,
			styleIds: selectAllFiltered ? [] : selectedStyleIds,
		});
	};

	const handleConfirmBulkPublish = async () => {
		const { selectAll, styleIds } = publishTargetRef.current;
		setBulkPublishLoading(true);
		setLoadingOpen(true);
		try {
			const { data } = await axios.post(`${MRP_SHEET_API}/bulk_publish`, {
				select_all: selectAll,
				style_ids: selectAll ? undefined : styleIds,
				filter: selectAll ? filterRef.current || {} : undefined,
			});
			setBulkPublishModal(false);
			clearSelection();
			setSnackBarOpen(true);
			setSnackBar((prev) => ({
				...prev,
				severity: "success",
				message: data?.message || "Styles published successfully",
			}));
			reloadStyles();
		} catch (error) {
			const errorData = error?.response?.data;
			setSnackBarOpen(true);
			setSnackBar((prev) => ({
				...prev,
				severity: "error",
				message: errorData?.message || parseErrorMessage(error),
			}));
		} finally {
			setBulkPublishLoading(false);
			setLoadingOpen(false);
		}
	};

	return (
		<Stack width="100%" pt={1} sx={{ bgcolor: "#fff" }}>
			<Box
				sx={{
					border: `1px solid ${palette.border}`,
					borderRadius: 2,
					bgcolor: palette.surface,
					px: { xs: 1.5, md: 2.5 },
					py: 2,
					overflow: "hidden",
				}}>
				{/* Filters */}
				<Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
					<Button
						size="small"
						variant="outlined"
						startIcon={<FilterAltRounded sx={{ fontSize: 16 }} />}
						onClick={handleFilterClick}
						sx={{
							textTransform: "none",
							fontSize: 12,
							color: palette.ink2,
							borderColor: activeFilterCount ? palette.ink2 : palette.border2,
							bgcolor: activeFilterCount ? palette.surface3 : "#fff",
							borderRadius: 1,
							minHeight: 32,
							fontWeight: activeFilterCount ? 600 : 500,
						}}>
						More Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
					</Button>
				</Box>

				{filterColumns?.length > 0 && (
					<Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" mb={1.5}>
						{filterColumns.map((row) => (
							<Chip
								key={row.key}
								size="small"
								variant="outlined"
								onDelete={() => removeActiveFilter(row.key)}
								label={`${row.name}: ${formatFilterChipValue(filterColumnVal[row?.key])}`}
								sx={{
									height: 26,
									fontSize: 11,
									borderRadius: 1,
									borderColor: palette.border2,
									bgcolor: palette.surface2,
									color: palette.ink2,
									maxWidth: 280,
									"& .MuiChip-label": {
										overflow: "hidden",
										textOverflow: "ellipsis",
									},
								}}
							/>
						))}
					</Box>
				)}

				{/* Toolbar */}
				<Box display="flex" alignItems="center" justifyContent="flex-end" gap={1.5} flexWrap="wrap" mb={1.25}>
					<Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mr: "auto" }}>
						{canPublishMrp && selectedCount > 0 && (
							<Typography sx={{ fontSize: 12, color: palette.ink3 }}>
								{selectAllFiltered
									? `Selected: ${selectedCount} (all matching filter)`
									: `Selected: ${selectedCount}`}
							</Typography>
						)}
						{canPublishMrp && selectedCount > 0 && (
							<Button
								variant="contained"
								color="success"
								size="small"
								onClick={handleBulkPublishClick}
								startIcon={<PublishRounded sx={{ fontSize: 16 }} />}
								sx={{ textTransform: "none", fontSize: 12 }}>
								Bulk Publish
							</Button>
						)}
						{canEditMrp && (
							<Button
								variant="contained"
								size="small"
								onClick={() => setUploadModal(true)}
								startIcon={<UploadRounded sx={{ fontSize: 16 }} />}
								sx={{ textTransform: "none", fontSize: 12 }}>
								Upload Data
							</Button>
						)}
					</Box>
					<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
						<ToolbarButton
							icon={<ViewColumnRounded sx={{ fontSize: 16 }} />}
							label="Columns"
							onClick={handleAddColumnClick}
						/>
						<ToolbarButton
							icon={<FileDownloadRounded sx={{ fontSize: 16 }} />}
							label="Export"
							onClick={handleDownloadClick}
						/>
						<FormControlLabel
							control={
								<Switch
									size="small"
									checked={displayImg}
									onChange={(e) => setDisplayImg(e.target.checked)}
									inputProps={{ "aria-label": "Toggle style images column" }}
									sx={{
										"& .MuiSwitch-switchBase.Mui-checked": { color: palette.ink },
										"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: palette.ink },
									}}
								/>
							}
							label={<Typography sx={{ fontSize: 12, color: palette.ink2 }}>Images</Typography>}
							sx={{ ml: 0.5, mr: 0 }}
						/>
					</Box>
				</Box>

				{/* Grid */}
				<Box sx={{ width: "100%", overflowX: "auto" }}>
					<DataGrid
						rows={styles || []}
						columns={styleTablColumns ?? []}
						autoHeight
						sx={{
							border: "none",
							width: "100%",
							fontSize: 12,
							color: palette.ink2,
							"& .MuiDataGrid-main": {
								overflowX: "auto",
								overflowY: "hidden",
							},
							"& .MuiDataGrid-columnHeaders": {
								bgcolor: palette.surface3,
								borderBottom: `1px solid ${palette.border2}`,
								minHeight: "40px !important",
								maxHeight: "40px !important",
							},
							"& .MuiDataGrid-columnHeader": {
								bgcolor: palette.surface3,
								color: palette.ink2,
								fontSize: 12,
								fontWeight: 600,
							},
							"& .MuiDataGrid-columnHeaderTitle": {
								fontWeight: 600,
								color: palette.ink2,
							},
							"& .MuiDataGrid-columnSeparator": {
								color: palette.border,
							},
							"& .MuiDataGrid-row": {
								cursor: "pointer",
								borderBottom: `1px solid ${palette.border}`,
								"&:nth-of-type(even)": { bgcolor: palette.surface2 },
								"&:hover": { bgcolor: palette.surface3 },
							},
							"& .MuiDataGrid-cell": {
								borderBottom: "none",
								fontSize: 12,
								py: 0.5,
							},
							"& .MuiDataGrid-footerContainer": { display: "none" },
							"& .MuiDataGrid-virtualScroller": {
								overflowX: "auto !important",
								overflowY: "hidden !important",
							},
							"& .MuiDataGrid-overlayWrapper": {
								minHeight: 120,
							},
						}}
						columnHeaderHeight={40}
						rowHeight={displayImg ? 56 : 40}
						getRowId={(row) => row?.style_id}
						onRowClick={(params) => handleStyleSelect(params.row)}
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={(model) =>
							setColumnVisibilityModel({
								...model,
								[styleImageColumn.key]: displayImg,
							})
						}
						disableRowSelectionOnClick
						hideFooter
						pagination={false}
						disableColumnSelector
						disableAutosize
						checkboxSelection={canPublishMrp}
						isRowSelectable={(params) => params.row.status === statusOptions.MRP_PENDING.key}
						rowSelectionModel={selectionModel}
						onRowSelectionModelChange={handleSelectionChange}
						slots={{
							noRowsOverlay: () => (
								<Box display="flex" alignItems="center" justifyContent="center" height="100%" minHeight={120}>
									{loading ? (
										<CircularProgress size={22} />
									) : (
										<Typography sx={{ fontSize: 13, color: palette.ink3 }}>No styles match your filters</Typography>
									)}
								</Box>
							),
						}}
					/>
				</Box>

				{/* Pagination */}
				<Box
					display="flex"
					alignItems="center"
					justifyContent="space-between"
					gap={1.5}
					flexWrap="wrap"
					pt={1.5}
					mt={0.5}
					borderTop={`1px solid ${palette.border}`}>
					<Typography sx={{ fontSize: 12, color: palette.ink3 }}>
						{tableStatusCount.total
							? `Showing ${showingFrom}–${showingTo} of ${formatCount(tableStatusCount.total)} · Published ${formatCount(tableStatusCount.published)} · Pending ${formatCount(tableStatusCount.pending)}`
							: "No results"}
					</Typography>
					{tableStatusCount.total > 0 && (
						<Box display="flex" alignItems="center" gap={0.5}>
							<IconButton
								size="small"
								aria-label="Previous page"
								disabled={page <= 1 || loading}
								onClick={() => goToPage(page - 1)}
								sx={{
									width: 28,
									height: 28,
									borderRadius: 1,
									border: `1px solid ${palette.border2}`,
								}}>
								<ChevronLeftRounded sx={{ fontSize: 16 }} />
							</IconButton>
							{pageButtons[0] > 1 && (
								<Typography sx={{ fontSize: 11, color: palette.ink4, px: 0.5 }}>…</Typography>
							)}
							{pageButtons.map((p) => (
								<Button
									key={p}
									size="small"
									onClick={() => goToPage(p)}
									disabled={loading}
									aria-current={p === page ? "page" : undefined}
									sx={{
										minWidth: 28,
										height: 28,
										p: 0,
										borderRadius: 1,
										fontSize: 12,
										fontWeight: p === page ? 700 : 500,
										color: p === page ? "#fff" : palette.ink2,
										bgcolor: p === page ? palette.ink : "transparent",
										border: `1px solid ${p === page ? palette.ink : palette.border2}`,
										"&:hover": {
											bgcolor: p === page ? palette.ink : palette.surface3,
										},
									}}>
									{p}
								</Button>
							))}
							{pageButtons[pageButtons.length - 1] < totalPages && (
								<Typography sx={{ fontSize: 11, color: palette.ink4, px: 0.5 }}>…</Typography>
							)}
							<IconButton
								size="small"
								aria-label="Next page"
								disabled={page >= totalPages || loading}
								onClick={() => goToPage(page + 1)}
								sx={{
									width: 28,
									height: 28,
									borderRadius: 1,
									border: `1px solid ${palette.border2}`,
								}}>
								<ChevronRightRounded sx={{ fontSize: 16 }} />
							</IconButton>
						</Box>
					)}
				</Box>
			</Box>

			{filterModal && (
				<FilterModal
					modal={filterModal}
					handleCloseModal={handleCloseFilterModal}
					filterColumns={filterColumns}
					setFilterColumns={setFilterColumns}
					filterColumnVal={filterColumnVal}
					setFilterColumnVal={setFilterColumnVal}
					localStorageStr={mrpSheetFilterValStr}
				/>
			)}
			{addColumnModal && (
				<AddColumnModal
					modal={addColumnModal}
					handleCloseModal={handleCloseAddColumnModal}
					attrColumns={attrColumns}
					setAttrColumns={setAttrColumns}
					localStorageStr={"styles_table_attr"}
				/>
			)}
			{editModal && (
				<EditDetailsModal
					modal={editModal}
					setModal={setEditModal}
					selectedStyle={selectedStyle}
					setSelectedStyle={setSelectedStyle}
					saveCallback={handleStyleUpdate}
					canEdit={canEditMrp}
					canPublish={canPublishMrp}
				/>
			)}
			{viewEditRemarkModal && selectedStyleForRemark?.remark_id && (
				<ViewEditRemarkModal
					modal={viewEditRemarkModal}
					setModal={setViewEditRemarkModal}
					styleData={selectedStyleForRemark}
					onSubmitSuccess={reloadStyles}
					disableEdit
					apiBase={MRP_SHEET_API}
				/>
			)}
			{canEditMrp && (
				<MrpUploadModal
					modal={uploadModal}
					setModal={setUploadModal}
					callbackFun={handleUploadSave}
					canPublish={canPublishMrp}
				/>
			)}
			<UploadResultModal modal={uploadResultModal} setModal={setUploadResultModal} result={uploadResult} />
			<DataExportModal
				modal={exportModal}
				handleCloseModal={handleCloseExportModal}
				filterColumnVal={filterColumnVal}
				exportTypeOptions={mrpSheetExportOptions}
			/>
			<Dialog
				open={!!zoomedImage}
				onClose={() => setZoomedImage(null)}
				maxWidth="md"
				PaperProps={{ sx: { bgcolor: palette.surface, borderRadius: 2, overflow: "hidden" } }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						px: 2,
						py: 1.25,
						borderBottom: `1px solid ${palette.border2}`,
					}}>
					<Box sx={{ minWidth: 0, flex: 1 }}>
						<Typography sx={{ fontSize: 13, fontWeight: 700, color: palette.ink, lineHeight: 1.3 }} noWrap>
							{zoomedImage?.styleName || "Style image"}
						</Typography>
						{zoomedImage?.styleId && (
							<Typography sx={{ fontSize: 11, color: palette.ink4 }}>{zoomedImage.styleId}</Typography>
						)}
					</Box>
					<IconButton
						size="small"
						aria-label="Open image in new tab"
						title="Open in new tab"
						onClick={() => window.open(zoomedImage?.url, "_blank", "noopener,noreferrer")}>
						<OpenInNewRounded sx={{ fontSize: 16 }} />
					</IconButton>
					<IconButton size="small" aria-label="Close image" onClick={() => setZoomedImage(null)}>
						<CloseRounded sx={{ fontSize: 18 }} />
					</IconButton>
				</Box>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						bgcolor: palette.surface3,
						p: 2,
					}}>
					<img
						src={zoomedImage?.url}
						alt={zoomedImage?.styleName || "Style"}
						style={{ maxWidth: "100%", maxHeight: "72vh", objectFit: "contain", display: "block" }}
					/>
				</Box>
			</Dialog>

			{canPublishMrp && (
				<BulkPublishModal
					modal={bulkPublishModal}
					setModal={setBulkPublishModal}
					previewStyles={bulkPublishPreview}
					selectAllFiltered={selectAllFiltered}
					loading={bulkPublishLoading}
					onConfirm={handleConfirmBulkPublish}
				/>
			)}
		</Stack>
	);
};

export default MrpSheet;
